import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { apiClient } from "../../../../lib/api";
import type { Goal, GoalFormState } from "../../types";
import { AccountabilitySection } from "./AccountabilitySection";
import { SlotEditor } from "./SlotEditor";

type Circle = Awaited<ReturnType<typeof apiClient.getCircles>>[number];

interface GoalDialogProps {
  readonly circles: Circle[] | undefined;
  readonly editingGoal: Goal | null;
  readonly form: GoalFormState;
  readonly formError: string | null;
  readonly isOpen: boolean;
  readonly isSaving: boolean;
  readonly onClose: () => void;
  readonly onSubmit: () => void;
  readonly setForm: (value: GoalFormState | ((previous: GoalFormState) => GoalFormState)) => void;
  readonly toggleSharedCircle: (circleId: string) => void;
}

export function GoalDialog({
  circles,
  editingGoal,
  form,
  formError,
  isOpen,
  isSaving,
  onClose,
  onSubmit,
  setForm,
  toggleSharedCircle,
}: GoalDialogProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingGoal ? "Edit goal" : "New goal"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <div className="space-y-1.5">
            <Label htmlFor="goal-name">Goal name</Label>
            <Input
              id="goal-name"
              autoFocus
              value={form.name}
              onChange={(event) => setForm((previous) => ({ ...previous, name: event.target.value }))}
              placeholder="e.g. Morning walk"
            />
          </div>

          <SlotEditor form={form} setForm={setForm} />
          <AccountabilitySection
            circles={circles}
            form={form}
            setForm={setForm}
            toggleSharedCircle={toggleSharedCircle}
          />

          {formError && <p className="text-sm text-destructive">{formError}</p>}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={isSaving}>
            {isSaving ? "Saving…" : editingGoal ? "Save changes" : "Create goal"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
